import type { JSX } from 'react';
import { useState } from 'react';
import { MoreHorizontal, Pencil, Ban, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SuspendConfirmDialog } from './SuspendConfirmDialog';
import type { Company } from '../types/company.types';

export interface CompanyActionsProps {
  company: Company;
  onEdit: (company: Company) => void;
  onSuspend?: ((id: string) => void) | undefined;
  onActivate?: ((id: string) => void) | undefined;
  showStatusActions?: boolean | undefined;
}

export function CompanyActions({
  company,
  onEdit,
  onSuspend,
  onActivate,
  showStatusActions = false,
}: CompanyActionsProps): JSX.Element {
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);

  const handleSuspendConfirm = (): void => {
    onSuspend?.(company.id);
    setShowSuspendConfirm(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" aria-label="Actions">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEdit(company)}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </DropdownMenuItem>
          {showStatusActions && (onSuspend || onActivate) && <DropdownMenuSeparator />}
          {showStatusActions && company.status === 'ACTIVE' && onSuspend && (
            <DropdownMenuItem variant="destructive" onClick={() => setShowSuspendConfirm(true)}>
              <Ban className="h-4 w-4 mr-2" />
              Suspend
            </DropdownMenuItem>
          )}
          {showStatusActions && company.status === 'SUSPENDED' && onActivate && (
            <DropdownMenuItem onClick={() => onActivate(company.id)}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Activate
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <SuspendConfirmDialog
        open={showSuspendConfirm}
        onOpenChange={setShowSuspendConfirm}
        companyName={company.name}
        onConfirm={handleSuspendConfirm}
      />
    </>
  );
}
